import { OrigensLogo } from '@origens/ui'
import type { ReactNode } from 'react'

/** Stable DOM ids for client portals into the SSR header shell (no useId). */
export const PORTAL_HEADER_TITLE_SLOT_ID = 'portal-header-title-slot'
export const PORTAL_HEADER_ACCOUNT_SLOT_ID = 'portal-header-account-slot'

function AccountChromeSkeleton() {
  return (
    <div
      className="flex h-9 w-9 items-center justify-center rounded-md"
      aria-hidden="true"
    >
      <span className="h-4 w-4 rounded-full border border-[var(--ink-muted)]" />
    </div>
  )
}

/**
 * Portable SSR header chrome: logo, title/account slots, and a space-reserving
 * account skeleton. Authenticated UI portals into the slots after hydration.
 */
export function PortalHeaderShell() {
  return (
    <header className="relative z-50 border-b border-[var(--border)] bg-[var(--card)]">
      <div className="flex w-full items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <a href="/" className="shrink-0">
            <OrigensLogo className="h-9 w-9" />
          </a>
          <a
            href="/"
            className="hidden text-base font-semibold tracking-tight text-[var(--ink)] no-underline sm:block"
          >
            Origens
          </a>
          <div id={PORTAL_HEADER_TITLE_SLOT_ID} className="min-w-0" />
        </div>

        <div className="flex h-9 shrink-0 items-center justify-end gap-2">
          <div className="relative flex h-9 w-9 items-center justify-end has-[#portal-header-account-slot:not(:empty)]:[&_.portal-header-account-skeleton]:hidden">
            <div className="portal-header-account-skeleton pointer-events-none absolute inset-0 flex items-center">
              <AccountChromeSkeleton />
            </div>
            <div
              id={PORTAL_HEADER_ACCOUNT_SLOT_ID}
              className="relative z-10 flex min-h-9 min-w-9 items-center justify-end"
            />
          </div>
        </div>
      </div>
    </header>
  )
}

/**
 * Page-owned SSR marker consumed by the persistent header with CSS `:has()`.
 * This lets request-time claims control static layout chrome without putting
 * Auth hooks stay out of the portable header.
 */
export function PortalRouteChrome({
  showDeployments,
  children,
}: {
  showDeployments: boolean
  children: ReactNode
}) {
  return (
    <div className="contents" data-portal-deployments-visible={showDeployments ? 'true' : 'false'}>
      {children}
    </div>
  )
}

export function PortalContentShell({ children }: { children: ReactNode }) {
  return (
    <main className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8 focus:outline-none sm:px-6">
      {children}
    </main>
  )
}

/** Portable navigation frame that reserves the exact desktop shell geometry before hydration. */
export function PortalNavigationFirstPaint({ children, isProject, isTeam, isMember, contextId, contextName, organizationName, canManageContext, isCurrentMember }: { children: ReactNode; isProject: boolean; isTeam: boolean; isMember: boolean; contextId: string; contextName: string; organizationName: string; canManageContext: boolean; isCurrentMember: boolean }) {
  return (
    <div className="min-h-[calc(100dvh-65px)] md:grid md:grid-cols-[15rem_minmax(0,1fr)]">
      <aside className="hidden p-3 pr-0 md:block">
        <div className="sticky top-3 flex min-h-[calc(100dvh-5.6rem)] flex-col rounded-xl border border-[var(--border)] bg-[var(--card)] p-4 shadow-sm">
          <p className="mb-5 px-3 text-sm font-semibold tracking-tight">{contextName || organizationName || 'Origens'}</p>
          <nav aria-label="Section navigation" className="grid gap-1">
            {isProject ? <>
              <a href={`/projects/${contextId}`} className="portal-nav-link">Overview</a>
              <a href={`/projects/${contextId}/deployments`} className="portal-nav-link">Deployments</a>
              <a href={`/projects/${contextId}/workflows`} className="portal-nav-link">Workflows</a>
              <a href={`/projects/${contextId}/usage`} className="portal-nav-link">Usage</a>
              <a href={`/projects/${contextId}/logs`} className="portal-nav-link">Logs</a>
              <a href={`/projects/${contextId}/previews`} className="portal-nav-link">Previews</a>
              {canManageContext ? <><a href={`/projects/${contextId}/settings`} className="portal-nav-link">General</a><a href={`/projects/${contextId}/git`} className="portal-nav-link">Git</a><a href={`/projects/${contextId}/environments`} className="portal-nav-link">Environments</a><a href={`/projects/${contextId}/variables`} className="portal-nav-link">Variables</a><a href={`/projects/${contextId}/firewall`} className="portal-nav-link">Firewall</a><a href={`/projects/${contextId}/advanced`} className="portal-nav-link">Advanced</a></> : null}
            </> : isTeam ? <>
              <a href={`/organization/teams/${contextId}`} className="portal-nav-link">Projects</a><a href={`/organization/teams/${contextId}/deployments`} className="portal-nav-link">Deployments</a><a href={`/organization/teams/${contextId}/teams`} className="portal-nav-link">Teams</a><a href={`/organization/teams/${contextId}/access`} className="portal-nav-link">Members</a><a href={`/organization/teams/${contextId}/general`} className="portal-nav-link">General</a><a href={`/organization/teams/${contextId}/advanced`} className="portal-nav-link">Advanced</a>
            </> : isMember ? <>
              <a href={`/members/${contextId}`} className="portal-nav-link">Profile</a>{isCurrentMember ? <><a href={`/members/${contextId}/organizations`} className="portal-nav-link">Organizations</a><a href={`/members/${contextId}/security`} className="portal-nav-link">Security</a><a href={`/members/${contextId}/teams`} className="portal-nav-link">Teams</a></> : null}
            </> : <><a href="/" className="portal-nav-link">Projects</a><a href="/deployments" className="portal-nav-link">Deployments</a><a href="/usage" className="portal-nav-link">Usage</a><a href="/logs" className="portal-nav-link">Logs</a><a href="/organization/teams" className="portal-nav-link">Teams</a><a href="/organization/members" className="portal-nav-link">Members</a><a href="/organization" className="portal-nav-link">General</a><a href="/organization/connections" className="portal-nav-link">Connections</a><a href="/organization/domains" className="portal-nav-link">Domains</a><a href="/organization/advanced" className="portal-nav-link">Advanced</a></>}
          </nav>
        </div>
      </aside>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

/** Deterministic portable document content for routes whose rich controls are client-owned. */
export function PortalSectionFirstPaint({
  title,
  description,
}: {
  title: string
  description: string
}) {
  return (
    <PortalContentShell>
      <header className="grid gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="text-sm text-[var(--ink-muted)]">{description}</p>
      </header>
    </PortalContentShell>
  )
}

/** Portable SSR shell when the session has no active organization. */
export function PortalOrgSelectShell() {
  return (
    <PortalContentShell>
      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] text-[var(--ink)] shadow-sm">
        <div className="flex flex-col gap-1.5 p-6">
          <h3 className="text-lg font-semibold leading-none tracking-tight">
            Select or create an organization
          </h3>
          <p className="text-sm text-[var(--ink-muted)]">
            Choose the organization you want to work in. Its projects, environments, and GitHub
            connections stay together.
          </p>
        </div>
      </div>
    </PortalContentShell>
  )
}
